// /src/app/(normal_header)/article/[slug]/ArticleClientContent.tsx (Client Component)
'use client'; // ファイルの先頭に記述

import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import Image from "next/image";
import styles from "./page.module.scss"; // Server Componentと同じCSS Moduleを参照
import { MyLink } from "@/components/MyLink";
import "zenn-content-css"; // CSSはこちらでimport
import { Sidebar } from "@/components/Sidebar";
import { TableOfContents } from "@/components/TableOfContents";
import { BreadCrumbs } from "@/components/BreadCrumbs";
import { SnsLinks } from "@/components/SnsLinks";
import { SnsLinksProfile } from "@/components/SnsLinksProfile";
import { RecommendList } from "@/components/RecommendList";
import { ContactAdd } from "@/components/ContactAdd";
import { createResizedImageUrl, getCategoryInfo } from "@/utils/utils"; // Clientで使うユーティリティ
import { Post } from "@/types/types";
import Script from "next/script";
import { SnsLinksVertical } from "@/components/SnsLinksVertical";
import { SwiperScripts } from "@/components/SwiperScripts";
import { BottomBanner3 } from "@/components/BottomBanner3";
import { BottomBanner4 } from "@/components/BottomBanner4";
import { StructuredData } from "@/components/StructuredData";

// EditorSocial型の定義
interface EditorSocial {
  icon: string;
  url: string;
}

// Schema.org関連の型定義を修正
interface SchemaListItem {
  "@type": "ListItem"; // リテラル型の指定方法を修正
  position: number;
  name: string;
  item?: string;
}

interface SchemaBreadcrumbList {
  "@context": "https://schema.org";
  "@type": "BreadcrumbList";
  itemListElement: SchemaListItem[];
}

interface SchemaNewsArticle {
  "@context": "https://schema.org";
  "@type": "NewsArticle";
  headline: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: Array<{
    "@type": "Person";
    name: string;
    url: string;
    sameAs: string[];
    description: string;
  }>;
  publisher: {
    "@type": "Organization";
    name: string;
    logo: {
      "@type": "ImageObject";
      url: string;
    };
  };
  mainEntityOfPage: {
    "@type": "WebPage";
    "@id": string;
  };
}

// Heading型 (目次用)
interface Heading {
  level: string;
  title: string;
  href: string;
}

// Client Component の Props 型定義
interface ArticleClientContentProps {
  post: Post;
  html: string;
  html2: string;
  headings: Heading[];
  categoryInfo: ReturnType<typeof getCategoryInfo>;
  filterdedCases: Post[];
  relatedPostSpecial?: Post;
  relatedPostSpecialImgSrc: string;
  otherRelatedPosts: Post[];
  hasTwitterLinks: boolean;
  hasSlider: boolean;
  params: { slug: string };
}

// Client Component 本体
export default function ArticleClientContent({
  post,
  html,
  html2,
  headings,
  categoryInfo,
  filterdedCases,
  relatedPostSpecial,
  relatedPostSpecialImgSrc,
  otherRelatedPosts,
  hasTwitterLinks,
  hasSlider,
  params,
}: ArticleClientContentProps) {
  const pathname = usePathname();
  const { data } = post;
  const youtubeID = data.youtubeID;
  const urlSlug = params.slug;
  
  // SecondCateの処理を改善
  const pageSecondCate = Array.isArray(data.SecondCate)
    ? data.SecondCate.filter((item): item is string => typeof item === 'string' && item.trim() !== '')
    : typeof data.SecondCate === 'string' && data.SecondCate.trim() !== ''
    ? [data.SecondCate]
    : [];
  
  const secondCate = pageSecondCate[0]; // Sidebarに渡す用

  // GAMターゲティング設定用のuseEffect
  useEffect(() => {
    // API準備チェック関数
    const checkGptApi = (callback: () => void, retryCount = 0) => {
      const maxRetries = 10;
      if (typeof window !== 'undefined' && window.googletag?.pubads) {
        callback();
      } else if (retryCount < maxRetries) {
        setTimeout(() => checkGptApi(callback, retryCount + 1), 100);
      } else {
        console.error('[GAM Page Targeting] Failed to initialize after max retries');
      }
    };

    const setPageLevelTargeting = () => {
      if (!pathname || typeof window === 'undefined' || !window.googletag?.cmd) {
        return;
      }

      window.googletag.cmd.push(() => {
        try {
          const pubads = window.googletag!.pubads();
          const fullPathKey = 'fullPath';
          const secondCateKey = 'secondCate';

          // 既存のターゲティングをクリア
          pubads.clearTargeting(fullPathKey);
          pubads.clearTargeting(secondCateKey);
          console.log(`[GAM Page Targeting] Cleared keys: ${fullPathKey}, ${secondCateKey}`);

          // fullPathの設定
          const fullPathValue = `/article/${params.slug}`;
          console.log(`[GAM Page Targeting] Setting ${fullPathKey}=${JSON.stringify([fullPathValue])}`);
          pubads.setTargeting(fullPathKey, [fullPathValue]);

          // secondCateの設定
          if (pageSecondCate.length > 0) {
            console.log(`[GAM Page Targeting] Setting ${secondCateKey}=${JSON.stringify(pageSecondCate)}`);
            pubads.setTargeting(secondCateKey, pageSecondCate);
          }

          // 広告のリフレッシュをトリガー
          pubads.refresh();
          console.log('[GAM Page Targeting] Triggered refresh after setting targeting');

        } catch (error) {
          console.error("[GAM Page Targeting] Error setting targeting:", error);
        }
      });
    };

    // 初期化とターゲティング設定
    if (typeof window !== 'undefined') {
      // グローバルなgoogletagオブジェクトを初期化
      window.googletag = window.googletag || {
        cmd: [],
        pubads: () => ({
          setTargeting: () => {},
          clearTargeting: () => {},
          refresh: () => {},
        }),
        defineSlot: () => ({
          addService: () => ({}),
        }),
        display: () => {},
        enableServices: () => {},
        destroySlots: () => {},
      } as unknown as typeof window.googletag;

      // ターゲティングを設定
      window.googletag?.cmd.push(() => {
        checkGptApi(setPageLevelTargeting);
      });
    }

    // クリーンアップ関数
    return () => {
      if (typeof window !== 'undefined' && window.googletag?.cmd) {
        window.googletag.cmd.push(() => {
          try {
            const pubads = window.googletag!.pubads();
            pubads.clearTargeting('fullPath');
            pubads.clearTargeting('secondCate');
            console.log('[GAM Page Targeting] Cleared all targeting on unmount');
          } catch (error) {
            console.error("[GAM Page Targeting] Error clearing targeting:", error);
          }
        });
      }
    };
  }, [pathname, params.slug, pageSecondCate]);

  // StructuredDataの部分を修正
  const structuredData: [SchemaNewsArticle, SchemaBreadcrumbList] = [
    {
      "@context": "https://schema.org",
      "@type": "NewsArticle",
      headline: data.title,
      description: data.description,
      image: data.featureImg || "https://www.ai-souken.com/sample.png",
      datePublished: data.date ? `${data.date}T00:00:00+09:00` : "",
      dateModified: data.updateDate 
        ? `${data.updateDate}T00:00:00+09:00` 
        : (data.date ? `${data.date}T00:00:00+09:00` : ""),
      author: [{
        "@type": "Person",
        name: data.editor_name || "",
        url: data.editor_social?.find((s: EditorSocial) => s.icon.includes('link'))?.url 
          || data.editor_social?.[0]?.url 
          || "",
        sameAs:
          data.editor_social?.map((social: { url: string }) => social.url).filter(Boolean) || [],
        description: data.editor_bio || ""
      }],
      publisher: {
        "@type": "Organization",
        name: "AI総合研究所",
        logo: {
          "@type": "ImageObject",
          url: "https://www.ai-souken.com/logo/logo.svg"
        }
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": `https://www.ai-souken.com/article/${params.slug}`
      }
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem" as const,
          position: 1,
          name: "お役立ち記事",
          item: "https://www.ai-souken.com/article"
        },
        ...(categoryInfo && categoryInfo[0] ? [{
          "@type": "ListItem" as const,
          position: 2,
          name: `${categoryInfo[0].firstCategoryJa}/${categoryInfo[0].secondCategoryJa}`,
          item: `https://www.ai-souken.com/article/category/${categoryInfo[0].secondCategoryEn || ""}`
        }] : []),
        {
          "@type": "ListItem" as const,
          position: categoryInfo && categoryInfo[0] ? 3 : 2,
          name: data.title
        }
      ]
    }
  ];

  // --- JSX Rendering ---
  return (
    <>
      <div>
        <BreadCrumbs
          data={[
            {
              name: categoryInfo[0]
                ? `${categoryInfo[0].firstCategoryJa}/${categoryInfo[0].secondCategoryJa}`
                : "",
              link: `/article/category/${
                categoryInfo[0]?.secondCategoryEn || ""
              }`,
            },
            { name: data.title },
          ]}
          dir="article"
        />
        <div className={styles.inner}>
          <main className={styles.main}>
            <div className={styles.articleWrap}>
              <SnsLinksVertical title={data.title} />
              <article>
                <div
                  className={styles.titleWrap}
                  style={youtubeID ? {} : { border: "none" }}
                >
                  <div className={styles.titleTopCategories}>
                    {categoryInfo.map((item) => (
                      <MyLink
                        href={`/article/category/${item.secondCategoryEn}`}
                        className={styles.titleWrapTop}
                        key={item.secondCategoryEn}
                      >
                        {`${item.firstCategoryJa}/${item.secondCategoryJa}`}
                      </MyLink>
                    ))}
                  </div>
                  <div className={styles.titleWrapDate}>
                    <Image
                      src="/icon/clock-icon2.svg"
                      alt=""
                      width={12}
                      height={11}
                    />
                    <time dateTime={data.updateDate || data.date}>
                      {data.updateDate || data.date || ""}
                    </time>
                  </div>
                  <h1 className={styles.title}>{data.title}</h1>
                  <div className={styles.titleWrapLinks}>
                    <SnsLinks title={data.title} />
                  </div>
                </div>
                {data.point && Array.isArray(data.point) ? (
                  <div className={styles.point}>
                    <p className={styles.pointAccenet}>
                      <span>この記事のポイント</span>
                    </p>
                    <ul className={styles.pointList}>
                      {data.point.map((item, index) => (
                        <li className={styles.pointItem} key={index}>
                          <Image
                            src="/icon/check-icon.svg"
                            alt=""
                            width={20}
                            height={20}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                    <div className={styles.pointBottom}>
                      <div className={styles.pointBottomImageWrap}>
                          <Image
                            src="https://aisouken.blob.core.windows.net/background/FacePhoto/FacePhoto-1.webp"
                            alt={data.editor_name || ""}
                            fill
                          />
                      </div>
                      <div className={styles.pointBottomNameWrap}>
                        <p>監修者プロフィール</p>
                        <p>{data.editor_name || ""}</p>
                        <p className={styles.pointBottomProfile}>
                          <a
                            href="https://x.com/LinkX_group"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "8px",
                              color: "#0066cc",
                              textDecoration: "underline",
                            }}
                          >
                            <img
                              src="/icon/x-icon.svg"
                              alt="Xでフォロー"
                              width={20}
                              height={20}
                              style={{ verticalAlign: "middle" }}
                            />
                            <span>フォローする</span>
                          </a>
                          <img
                           src="/icon/MicrosoftMVP.png"
                           alt="MicrosoftMVP"
                           style={{
                             marginTop: "20px",
                             marginLeft: "-30px",
                             width: "150px",          // コンテナに合わせて広がる
                             maxWidth: "100%",      // 最大幅を制限（お好みで）
                             height: "auto",         // アスペクト比維持
                             display: "block",
                           }}
                         />
                        </p>
                      </div>
                      <p className={styles.pointBottomProfile}>
                          {data.editor_bio || ""}
                      </p>
                    </div>
                  </div>
                ) : null}
                {youtubeID && (
                  <div className={styles.youtube}>
                    <div className={styles.youtubeVideoWrap}>
                      <iframe
                        width="810"
                        height="500"
                        src={`https://www.youtube-nocookie.com/embed/${youtubeID}`}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                        className={styles.youtubeVideo}
                      />
                    </div>
                  </div>
                )}
                <div
                  className="znc"
                  dangerouslySetInnerHTML={{ __html: html2 }}
                  style={{ marginBottom: "3rem" }}
                />
                <TableOfContents headings={headings} />
                <div
                  className="znc"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </article>
            </div>
            {data.tags && data.tags.filter(Boolean).length ? (
              <div className={styles.bottomLinks}>
                <div className={styles.tags}>
                  {data.tags.map((tag: string) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            ) : null}
            <div
              className={styles.serviceDocument}
              style={{ marginTop: "8rem" }}
            >
              <div className={styles.serviceDocumentLeft}>
                <p className={styles.serviceDocumentText1}>
                  AI活用のノウハウ集「AI総合研究所」サービスご紹介資料
                </p>
                <p className={styles.serviceDocumentText2}>
                  「AI総合研究所　サービス紹介資料」は、AI導入のノウハウがないというお客様にも使いやすい最先端のAI導入ノウハウを知れる資料です。
                </p>
                <a
                  href="/resources/aisouken-introduction-resources"
                  className={styles.serviceDocumentLink}
                >
                  資料ダウンロード
                </a>
              </div>
              <Image
                src="https://aisouken.blob.core.windows.net/resource/3/resource-aisouken-introduction.webp"
                alt="AI総合研究所サービス紹介資料"
                width={288}
                height={162}
                className={styles.serviceImage}
              />
            </div>
            <div className={styles.snsLinks}>
              <SnsLinks title={data.title} />
            </div>
            <dl className={styles.relater}>
              <div className={styles.editor}>
                <dt>監修者</dt>
                <dd>
                  <div className={styles.personImageWrap}>
                    <Image
                      src="https://aisouken.blob.core.windows.net/background/FacePhoto/FacePhoto-1.webp"
                      alt={data.editor_name || ""}
                      fill
                      className={styles.personImage}
                    />
                  </div>
                  <div>
                    <p className={styles.name}>{post.data.editor_name}</p>
                    <p className={styles.nameText}>{post.data.editor_bio}</p>
                    <div className={styles.snsLinks}>
                      <SnsLinksProfile data={data} />
                    </div>
                  </div>
                </dd>
              </div>
            </dl>
          </main>

          <Sidebar
            headings={headings}
            dir="article"
            adImage1={categoryInfo[0]?.adImage1}
            adImage2={categoryInfo[0]?.adImage2}
            urlSlug={urlSlug}
            secondCate={secondCate}
          />
        </div>
        {/* Related Posts Section */}
        {relatedPostSpecial && (
          <div className={styles.relatedPost}>
            <p className={styles.relatedPostText}>関連記事</p>
            <div className={styles.relatedPosts}>
              {/* Special Related Post */}
              <div className={styles.relatedPostSpecialItem}>
                <MyLink href={"/article/" + relatedPostSpecial.slug}>
                  <div className={styles.relatedPostSpecialImage}>
                    <Image
                      src={relatedPostSpecialImgSrc || "/sample.png"}
                      alt={relatedPostSpecial.data.title || ""}
                      fill
                    />
                  </div>
                </MyLink>
                <div>
                  {getCategoryInfo(relatedPostSpecial.data.SecondCate, "article").map((item) => (
                    <MyLink
                      href={`/article/category/${item.secondCategoryEn}`}
                      className={styles.relatedPostSpecialTop}
                      key={item.secondCategoryEn}
                    >
                      {`${item.firstCategoryJa}/${item.secondCategoryJa}`}
                    </MyLink>
                  ))}
                </div>
                <MyLink
                  href={"/article/" + relatedPostSpecial.slug}
                  className={styles.relatedPostSpecialTitleLink}
                >
                  <h2 className={styles.relatedPostSpecialTitle}>
                    {relatedPostSpecial.data.title || ""}
                  </h2>
                </MyLink>
                <div className={styles.relatedPostDate}>
                  <Image
                    src="/icon/clock-icon2.svg"
                    alt=""
                    width={9}
                    height={9}
                  />
                   <time dateTime={relatedPostSpecial.data.updateDate || relatedPostSpecial.data.date}>
                    {relatedPostSpecial.data.updateDate || relatedPostSpecial.data.date || ""}
                   </time>
                </div>
              </div>
              {/* Other Related Posts */}
              <ul className={styles.relatedPostList}>
                {otherRelatedPosts.map((related) => {
                  const path = "/article/" + related.slug;
                  const imgSrc = related.data.featureImg?.startsWith(
                    "https://aisouken.blob.core.windows.net/article/"
                  )
                    ? createResizedImageUrl(related.data.featureImg, 800, 450)
                    : related.data.featureImg;
                  return (
                    <li className={styles.relatedPostItem} key={related.slug}>
                      <MyLink href={path}>
                        <div className={styles.relatedPostImage}>
                          <Image
                            src={imgSrc || "/sample.png"}
                            alt={related.data.title || ""}
                            fill
                          />
                        </div>
                      </MyLink>
                      <div>
                        {getCategoryInfo(related.data.SecondCate, "article").map(
                          (item) => (
                            <MyLink
                              href={`/article/category/${item.secondCategoryEn}`}
                              className={styles.relatedPostTop}
                              key={item.secondCategoryEn}
                            >
                              {`${item.firstCategoryJa}/${item.secondCategoryJa}`}
                            </MyLink>
                          )
                        )}
                      </div>
                      <MyLink
                        href={path}
                        className={styles.relatedPostTitleLink}
                      >
                        <h2 className={styles.relatedPostTitle}>
                          {related.data.title || ""}
                        </h2>
                      </MyLink>
                      <div className={styles.relatedPostDate}>
                        <Image
                          src="/icon/clock-icon.svg"
                          alt=""
                          width={9}
                          height={9}
                        />
                        <time dateTime={related.data.updateDate || related.data.date}>
                          {related.data.updateDate || related.data.date || ""}
                        </time>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
        {/* Recommended Cases Section */}
        <div className={styles.recommend}>
          <div className={styles.recommendInner}>
            <p className={styles.recommendText}>あなたにおすすめの事例</p>
            <RecommendList posts={filterdedCases} />
            <MyLink href="/case" className={styles.smapleMoreLink}>
              もっと見る
            </MyLink>
          </div>
        </div>
        <ContactAdd />
        <BottomBanner3 />
        <BottomBanner4 />
        {/* Conditionally load scripts */}
        {hasTwitterLinks && (
          <Script src="https://platform.twitter.com/widgets.js" strategy="lazyOnload" />
        )}
        {hasSlider && (
          <>
            <SwiperScripts />
            <link
              rel="stylesheet"
              href="https://cdn.jsdelivr.net/npm/swiper@11/swiper-bundle.min.css"
            />
          </>
        )}
      </div>
      <StructuredData jsonLd={structuredData} />
    </>
  );
}

// Export the Client Component (これが ArticleClientContent.tsx ファイルのデフォルトエクスポートになります)
// export default ArticleClientContent;